import React, { Fragment } from 'react'
import { withRouter } from 'react-router'

import { useUnit } from 'effector-react'

import { useCallback, useEffect, useMediaQueries, useRef } from 'hooks'

import { urlTo } from 'helpers/router.js'

import { $activeTransferRequestsLoading } from 'app/effector/nominees-transfers/request/sipp'

import GlobalPreloader from 'components/_old/GlobalPreloader/GlobalPreloader.jsx'
import Inner from 'components/_old/Inner/Inner.jsx'
import { Typo } from 'components/_old/Typo/Typo'
import Width from 'components/_old/Width/Width'

import { GatewayDest } from 'components/atoms/Gateway'
import { DesktopLayout, MobileLayout } from 'components/atoms/Layouts'
import { Paper } from 'components/atoms/Paper'

import { AnimatedNavigationBar } from 'components/molecules/AnimatedNavigationBar'
import ColumnarLayout, { Column } from 'components/molecules/ColumnarLayout/ColumnarLayout.jsx'

import { DesktopFooter } from 'app/containers/Footer'
import { DesktopHeader } from 'app/containers/Header'

import { Sticky } from '../Goals/DIY/EditWeights/components/Sticky'

import { BackLink, Form, Submit } from './components'

import { setPrevPage, setNextPage, closePage, reset, initForm, setShouldRedirectToTransferCentre } from './index'

type TransferSippProps = {
  location: {
    query: {
      prev?: string
      next?: string
      noRedirect?: string
    }
  }
}

const TransferSipp = withRouter(({ location }: TransferSippProps): React.ReactElement => {
  const { desktop } = useMediaQueries()
  const titleRef = useRef<HTMLElement>()
  const activeTransferRequestsLoading = useUnit($activeTransferRequestsLoading)

  const handleMount = useCallback(() => {
    const defaultUrl = urlTo('dashboard')
    const prevUrl = location.query.prev ?? defaultUrl
    const nextUrl = location.query.next ?? defaultUrl

    setPrevPage(prevUrl)
    setNextPage(nextUrl)
    setShouldRedirectToTransferCentre(location.query?.noRedirect !== 'true')

    initForm()
  }, [location.query])

  useEffect(() => {
    handleMount()

    return () => {
      reset()
    }
  }, [handleMount])

  if (activeTransferRequestsLoading) {
    return <GlobalPreloader loading />
  }

  const desktopLayout = (
    <DesktopLayout
      header={<DesktopHeader />}
      content={
        <Fragment>
          <Inner>
            <ColumnarLayout>
              <Column size={5}>
                <Paper top={8}>
                  <BackLink />
                </Paper>
              </Column>
              <Column size={22}>
                <Paper left={8}>
                  <Width size={45}>
                    <Form titleRef={titleRef} />
                    <Sticky marginTop={56} bottom={24} zIndex={1001} center>
                      <Width size={24} data-test-id="submitButton">
                        <Submit />
                      </Width>
                    </Sticky>
                  </Width>
                </Paper>
              </Column>
            </ColumnarLayout>
          </Inner>
        </Fragment>
      }
      footer={<DesktopFooter />}
    />
  )

  const mobileLayout = (
    <MobileLayout
      header={
        <AnimatedNavigationBar nodeToWatchTo={titleRef?.current} leftPartText="Back" onLeftPartClick={closePage}>
          <Typo>Pension transfer</Typo>
        </AnimatedNavigationBar>
      }
      content={
        <Fragment>
          <Form />
          <Sticky marginTop={48} bottom={16} zIndex={1001}>
            <Submit />
          </Sticky>
        </Fragment>
      }
    />
  )

  return (
    <Fragment>
      {desktop ? desktopLayout : mobileLayout}
      <GatewayDest name="toasts" />
      <GatewayDest name="modals" />
    </Fragment>
  )
})
TransferSipp.displayName = 'TransferSipp'

export { TransferSipp }
